/**
 * Shared payload hygiene for Sentry ignored-drop telemetry (BAL-856, BAL-891).
 *
 * The browser, Node and Edge halves all write the SAME `sentry_event_dropped`
 * PostHog event, so they must sanitize identically. Keeping these helpers in
 * one module is what stops the merged table from being redacted on some rows
 * and raw on others.
 *
 * Everything here is defensive by default: a value is redacted unless it is
 * recognisably safe, never kept unless it is recognisably dangerous. The drop
 * path is fed by arbitrary upstream error text, so an allowlist is the only
 * shape that stays correct as new error sources appear.
 */

/**
 * Telemetry payload for an event discarded by `IGNORED_ERROR_PATTERNS`.
 *
 * `beforeSend` returns null on a drop, so without this the only surviving
 * evidence is Sentry's aggregate `client_discard` counter — which cannot be
 * grouped by pattern, route, or environment.
 */
export interface IgnoredDropInfo {
  /** `RegExp.source` of the matched `IGNORED_ERROR_PATTERNS` entry. */
  pattern: string;
  /** First 120 chars of the error message, PII-redacted. */
  messagePrefix: string;
  /** Request route — normalized, no query string, no opaque segments. */
  route: string;
  /**
   * `production` | `preview` | `development` | … — see `resolveEnvironment`.
   *
   * Sentry's own discard counter (`stats_v2`) cannot be filtered or grouped by
   * environment: `&environment=` is silently ignored and `&groupBy=environment`
   * is rejected outright. So a drop is unattributable to prod vs preview by any
   * route unless the payload carries the field itself (BAL-891).
   */
  environment: string;
}

export type IgnoredDropHandler = (drop: IgnoredDropInfo) => void;

/** Max chars of error message forwarded — enough to identify the class. */
export const MESSAGE_PREFIX_LENGTH = 120;

/**
 * Hard bound on how much of a message the redaction pass will scan.
 *
 * Redaction has to run BEFORE truncation, or a secret could be cut in half
 * and its tail shipped unredacted. But `Error.message` is unbounded — Sentry's
 * `maxValueLength` only rewrites `event.exception.values[].value`, not the raw
 * thrown error — so "redact everything, then keep 120" means an arbitrarily
 * long synchronous scan inside `beforeSend`, on the request path.
 *
 * Scanning the first 1 KB is safe: only `MESSAGE_PREFIX_LENGTH` chars are ever
 * forwarded, so anything a truncation at 1 KB could split is already ~900
 * chars past the end of what ships.
 */
const MAX_REDACTION_SCAN_LENGTH = 1_000;

/** Max chars of route forwarded, after normalization. */
const MAX_ROUTE_LENGTH = 120;

/**
 * Values that must never leave the process inside a telemetry payload.
 *
 * The ignore-list only matches benign classes, but patterns like
 * `/network error/i` and `/rate limit/i` match as a SUBSTRING anywhere in a
 * message, so arbitrary upstream error text reaches `messagePrefix` — a
 * Postgres error carrying a row value (`Key (email)=(dancer@example.com)`), a
 * vendor error echoing a request, and so on.
 */
const PII_REDACTIONS: Array<[RegExp, string]> = [
  // The local part is bounded at RFC 5321's 64-char maximum. Unbounded
  // (`[\w.+-]+@`) this is quadratic: on a long unbroken run of word/dot/plus/
  // hyphen characters with no `@`, the engine retries every suffix from every
  // start position. Measured through this module: 4 KB → 26 ms, 32 KB →
  // 3.9 s, synchronously inside `beforeSend`. Base64url blobs, hex digests
  // and encoded bodies are all exactly that shape.
  [/[\w.+-]{1,64}@[\w-]+\.[\w.-]+/g, '<email>'],
  [
    /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi,
    '<uuid>',
  ],
  // JWTs. Case-insensitive because one server call site lowercases the
  // exception value before it reaches here, which would otherwise make this
  // rule silently dead on that path.
  [/\beyJ[\w-]{8,}\.[\w-]+\.[\w-]+/gi, '<jwt>'],
  // Vendor keys, underscore-delimited: Supabase, Stripe, Resend, PostHog,
  // GitHub, webhook secrets.
  [
    /\b(?:sb_secret|sb_publishable|sk_live|sk_test|pk_live|whsec|re|phc|phx|gh[pousr])_[A-Za-z0-9_-]{6,}/gi,
    '<secret>',
  ],
  // Vendor keys, HYPHEN-delimited. A separate rule because the underscore
  // pattern above cannot reach them, and these are the ones that show up on
  // the `/rate limit/i` drop path — AI-provider 429s are exactly that class.
  [/\b(?:sk-ant|sk-proj|sk|xox[baprs])-[A-Za-z0-9_-]{8,}/gi, '<secret>'],
  // AWS access key ids.
  [/\b(?:AKIA|ASIA)[A-Z0-9]{12,}/g, '<secret>'],
  [/\bBearer\s+[A-Za-z0-9._-]{8,}/gi, 'Bearer <token>'],
  // Phone numbers written with separators — `\d{7,}` below cannot see them.
  [/\+\d[\d\s().-]{7,}\d/g, '<phone>'],
  // Long hex runs — session ids, HMACs, digests.
  [/\b[0-9a-f]{16,}\b/gi, '<hex>'],
  [/\b\d{7,}\b/g, '<num>'],
];

/** Redact known-sensitive substrings. Always run BEFORE truncating. */
export function redactPii(value: string): string {
  return PII_REDACTIONS.reduce(
    (acc, [pattern, replacement]) => acc.replace(pattern, replacement),
    value,
  );
}

/**
 * A Next.js dynamic-route segment: `[id]`, `[...slug]`, `[[...params]]`.
 * Anchored and restricted to identifier characters so a concrete URL segment
 * that merely contains brackets cannot pose as a template.
 */
const TEMPLATE_SEGMENT = /^\[{1,2}\.{0,3}[a-z0-9_]+\]{1,2}$/i;

const UUID_SEGMENT =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Numeric ids — row ids, years, pagination. */
const NUMERIC_SEGMENT = /^\d{4,}$/;

/**
 * Slug shape: lowercase words joined by hyphens. Covers static route names
 * (`invoices`, `settings`) and public SEO slugs / handles (`natalie-vikner`,
 * `swiss-dance-awards`).
 */
const SLUG_SEGMENT = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * Credential shape: a long unbroken run mixing letters and digits. Catches
 * hex digests and base64url tokens that would otherwise read as one long
 * "slug" — a lowercase UUID and a 32-char hex session id both satisfy
 * SLUG_SEGMENT, so shape alone is not enough.
 */
const OPAQUE_SEGMENT =
  /^(?=.{12,})(?=[a-z0-9]*\d)(?=[a-z0-9]*[a-z])[a-z0-9]+$/i;

/** True when a path segment may be forwarded verbatim. */
function isSafeSegment(segment: string): boolean {
  if (UUID_SEGMENT.test(segment)) return false;
  if (NUMERIC_SEGMENT.test(segment)) return false;
  if (OPAQUE_SEGMENT.test(segment)) return false;
  return SLUG_SEGMENT.test(segment) && segment.length < 40;
}

/**
 * Route prefixes whose dynamic segment IS a credential. Everything after one
 * of these is redacted regardless of shape.
 *
 * The shape allowlist above already catches today's tokens (a `/m/` token is
 * 16 chars of base64url, so it is mixed-case and opaque). This is the second
 * layer, because shape heuristics are probabilistic and these routes are
 * known-sensitive by construction: a short all-lowercase token would satisfy
 * the slug rule and slip through on shape alone.
 *
 * Keep in sync with the `[token]` / `[code]`-style dynamic routes under
 * `apps/web/app`.
 */
const CREDENTIAL_ROUTE_PREFIXES = [
  '/m/', // magic-link passwordless login
  '/r/', // referral / short code
  '/feedback/', // per-recipient feedback token
  '/auth/invitation/', // admin invitation token
];

/**
 * Reduce a path to a low-cardinality, credential-free route.
 *
 * WHY an allowlist and not a blocklist: the first version of this normalized
 * only UUIDs and long digit runs, and passed everything else through. That
 * leaked `/m/<token>` — a 12-byte base64url passwordless-login credential
 * (`apps/web/app/m/[token]/route.ts`) that is neither a UUID nor numeric — into
 * a third-party analytics store. Any segment that is not recognisably safe is
 * now replaced, so a future route carrying a secret in its path fails closed.
 */
export function normalizeRoutePath(pathname: string): string {
  // A route template (`/m/[token]`) names the parameter instead of its value,
  // so it is already safe and strictly more useful than `:redacted`.
  //
  // The check is STRICT because a concrete request URL can also contain
  // brackets: WHATWG `URL` preserves them in `pathname`, so a loose test let
  // an unauthenticated caller write `/m/[$(whoami)]` straight into the
  // telemetry payload AND skip the credential-prefix rule below.
  // Scoped to the segment the prefix actually governs, not the whole path: a
  // bracket ANYWHERE would otherwise disable the credential rule for every
  // other segment, so `/m/<token>/[x]` would ship the token.
  const credentialPrefix = CREDENTIAL_ROUTE_PREFIXES.find((prefix) => {
    if (!pathname.toLowerCase().startsWith(prefix)) return false;
    const governed = pathname.slice(prefix.length).split('/')[0] ?? '';
    return !TEMPLATE_SEGMENT.test(governed);
  });
  if (credentialPrefix) {
    // Keep the prefix — it identifies WHERE drops happen, which is the whole
    // point of the field — and discard everything after it.
    return `${credentialPrefix}:redacted`;
  }

  const normalized = pathname
    .split('/')
    .map((segment) => {
      if (segment === '') return segment;
      // Next.js route templates (`[token]`, `[...slug]`) carry no value.
      if (TEMPLATE_SEGMENT.test(segment)) return segment;
      return isSafeSegment(segment) ? segment : ':redacted';
    })
    .join('/');

  return normalized.length > MAX_ROUTE_LENGTH
    ? normalized.slice(0, MAX_ROUTE_LENGTH)
    : normalized;
}

/**
 * Derive a safe route from a Sentry event.
 *
 * Prefers the Next.js route TEMPLATE when the event carries one —
 * `instrumentation.ts`'s `onRequestError` tags every server error with
 * `context.routePath`, e.g. `/m/[token]`. A template is inherently safe: it
 * names the parameter instead of its value. Falls back to normalizing the
 * concrete URL.
 */
export function extractRoute(
  url: string | undefined,
  taggedRoute?: unknown,
): string {
  if (typeof taggedRoute === 'string' && taggedRoute.includes('[')) {
    return normalizeRoutePath(taggedRoute);
  }

  if (!url) return 'unknown';

  try {
    // The base makes a bare pathname parseable; the query string is dropped
    // wholesale (auth tokens, emails and search terms all live there).
    const { pathname } = new URL(url, 'http://localhost');
    return normalizeRoutePath(pathname);
  } catch {
    return 'unknown';
  }
}

/** Build the sanitized payload. Single place both halves agree on. */
export function buildDropInfo(args: {
  pattern: string;
  errorValue: string;
  route: string;
  environment: string;
}): IgnoredDropInfo {
  return {
    pattern: args.pattern,
    messagePrefix: redactPii(
      args.errorValue.slice(0, MAX_REDACTION_SCAN_LENGTH),
    ).slice(0, MESSAGE_PREFIX_LENGTH),
    route: args.route,
    environment: args.environment,
  };
}
